import { Action, ActionCreator, Reducer } from 'redux';
import { ThunkAction } from 'redux-thunk';
// eslint-disable-next-line import/no-cycle
import { checkScenario } from '@mfe/util/scenario';
import { assert } from '@mfe/util/helpers';
import sysConfig from '../config';
import loadScript from '../util/loadScript';
import { TefComs } from '../types/typings';
import type { RootState } from './index';

declare const window: {
  one?: { // tef-one
    ready?: unknown;
  };
} & Window;

// action types

export const INIT = 'config/INIT';
export const OCCL = 'config/OCCL';
export const ERROR = 'config/ERROR';

// initial state

export interface ConfigState extends TefComs.ConfigOptions {
  error: boolean;
  occlThemeLoaded: boolean;
  occlVendorsLoaded: boolean;
  occlBundleLoaded: boolean;
  occlReady: boolean;
}

// action creators

export const init: ActionCreator<ThunkAction<Promise<void>, RootState, void, Action>> = (
  config: ConfigState,
) => async (
  dispatch,
// eslint-disable-next-line @typescript-eslint/require-await
) => {
  const { apiUri: apiUriRaw, occluri: occluriRaw } = config;
  const normalize = (uri: string) => (uri ? uri.replace(/\/$/, '') : undefined);
  const apiUri = normalize(apiUriRaw);
  const occluri = normalize(occluriRaw);

  dispatch({
    type: INIT,
    ...config,
    apiUri,
    occluri,
  });

  try {
    const {
      spId, lcId, registerId,
      customerToken, accessToken, registerToken, accesstoken4p,
      lifecycle, mode, apiUri4p,
    } = config;
    const { isRaittNoTokensWithInterception } = checkScenario(config);

    assert(lifecycle, 'lifecycle is required');
    assert(apiUri, 'apiuri is required');
    assert(
      !(registerToken && customerToken),
      'registertoken and customertoken are mutually exclusive',
    );
    assert(
      !isRaittNoTokensWithInterception || spId,
      'if no tokens are provided (RAITT), spId is required',
    );
    assert(
      !isRaittNoTokensWithInterception || lcId || registerId,
      'if no tokens are provided (RAITT), lcId or registerId is required',
    );
    assert(
      isRaittNoTokensWithInterception || !(customerToken && !accessToken),
      'customertoken requires accesstoken',
    );
    assert(
      !(accesstoken4p && !apiUri4p),
      'accesstoken4p requires apiuri4p',
    );
    assert(
      !(accesstoken4p && !accessToken),
      'accesstoken4p requires accesstoken',
    );
    assert(
      occluri || window.one?.ready,
      'occluri is required unless tef-one is present',
    );
    assert(
      /^((multiedit(:.+)?(,buttons:.*)?)|(buttons(:(save|saveActive|cancel))?)|(merge))?$/.test(mode),
      'mode is invalid',
    );
  } catch (error) {
    dispatch({ type: ERROR, error });
    throw error;
  }

  if (occluri) {
    // load occl libraries
    const {
      occl: { vendors, bundle },
    } = sysConfig;
    const onLoadVendors = () => dispatch({ type: OCCL, occlVendorsLoaded: true });
    const onLoadBundle = () => dispatch({ type: OCCL, occlBundleLoaded: true });
    const onError = () => dispatch({ type: ERROR, error: new Error('loading OCCL failed') });
    const onErrorDummy = () => {};
    vendors(occluri).forEach((lib) => {
      const errorHandler = lib.ignoreNotFound ? onErrorDummy : onError;
      loadScript({ ...lib, onLoad: onLoadVendors, onError: errorHandler });
    });

    bundle(occluri).forEach((lib) => {
      loadScript({ ...lib, onLoad: onLoadBundle, onError });
    });
  }
};

export const occlThemeLoad: ActionCreator<ThunkAction<Promise<Action>, RootState, void, Action>> = () => async (
  dispatch,
// eslint-disable-next-line @typescript-eslint/require-await
): Promise<Action> => dispatch({ type: OCCL, occlThemeLoaded: true });

export const occlThemeError: ActionCreator<ThunkAction<Promise<Action>, RootState, void, Action>> = () => async (
  dispatch,
// eslint-disable-next-line @typescript-eslint/require-await
): Promise<Action> => dispatch({ type: ERROR, error: new Error('loading OCCL failed') });

type ConfigAction = Action<typeof ERROR | typeof INIT | typeof OCCL> & ConfigState;

const initial: Omit<ConfigState, 'newcustomer' | 'lifecycle' | 'apiUri' | 'occluri'> = {
  error: false,
  occlThemeLoaded: false,
  occlVendorsLoaded: false,
  occlBundleLoaded: false,
  occlReady: false,
};

// reducer

export const reducer: Reducer<ConfigState, ConfigAction> = (
  state = initial as ConfigState,
  action = {} as ConfigAction,
) => {
  const {
    type,
    accessToken,
    customerToken,
    newcustomer,
    registerToken,
    lifecycle,
    apiUri,
    apiUri4p,
    accesstoken4p,
    mode,
    occluri,
    stylecontext,
    channel,
    vonr,
    traceid,
    references,
    comment,
    usertype,
    occlThemeLoaded,
    occlVendorsLoaded,
    occlBundleLoaded,
    error,
    spId,
    lcId,
    registerId,
  } = action;
  switch (type) {
    case ERROR:
      return {
        ...state,
        error,
      };
    case INIT:
      return {
        ...state,
        accessToken,
        customerToken,
        newcustomer,
        registerToken,
        lifecycle,
        apiUri,
        apiUri4p,
        accesstoken4p,
        mode,
        occluri,
        stylecontext,
        channel,
        vonr,
        traceid,
        references,
        comment,
        usertype,
        spId,
        lcId,
        registerId,
      };
    case OCCL: {
      const newOcclThemeLoaded = !!(state.occlThemeLoaded || occlThemeLoaded);
      const newOcclVendorsLoaded = !!(
        state.occlVendorsLoaded || occlVendorsLoaded
      );
      const newOcclBundleLoaded = !!(
        state.occlBundleLoaded || occlBundleLoaded
      );
      const newOcclReady = newOcclThemeLoaded && newOcclVendorsLoaded && newOcclBundleLoaded;
      return {
        ...state,
        occlThemeLoaded: newOcclThemeLoaded,
        occlVendorsLoaded: newOcclVendorsLoaded,
        occlBundleLoaded: newOcclBundleLoaded,
        occlReady: newOcclReady,
      };
    }
    default:
      return state;
  }
};
